import { ref } from 'vue'
import type { Ref } from 'vue'
import type { Sponsor, SponsorFormData } from '@/types/sponsors'
import {
  listSponsors as apiListSponsors,
  createSponsor as apiCreateSponsor,
  updateSponsor as apiUpdateSponsor,
  deleteSponsor as apiDeleteSponsor,
  uploadSponsorLogo as apiUploadSponsorLogo,
  deleteSponsorLogo as apiDeleteSponsorLogo,
} from '@/api/sponsors'

const sponsors = ref<Sponsor[]>([])
const loading = ref(false)
const saving = ref(false)
const logoUploading = ref(false)
const errors = ref<Record<string, string[]>>({})
const generalError = ref<string | null>(null)

function isValidationError(
  e: unknown,
): e is { response: { data: { errors: Record<string, string[]> } } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: { data?: unknown } }).response !== null &&
    typeof (e as { response: { data: unknown } }).response.data === 'object'
  )
}

async function fetchSponsors(eventSlug: string, editionSlug: string): Promise<void> {
  loading.value = true
  generalError.value = null
  try {
    sponsors.value = await apiListSponsors(eventSlug, editionSlug)
  } catch {
    generalError.value = 'editions.sponsors.errors.load_failed'
  } finally {
    loading.value = false
  }
}

async function createSponsor(
  eventSlug: string,
  editionSlug: string,
  data: SponsorFormData,
): Promise<Sponsor | null> {
  saving.value = true
  errors.value = {}
  generalError.value = null
  try {
    const created = await apiCreateSponsor(eventSlug, editionSlug, data)
    sponsors.value = [...sponsors.value, created]
    return created
  } catch (e: unknown) {
    if (isValidationError(e)) {
      errors.value = e.response.data.errors
    } else {
      generalError.value = 'editions.sponsors.errors.save_failed'
    }
    return null
  } finally {
    saving.value = false
  }
}

async function updateSponsor(
  eventSlug: string,
  editionSlug: string,
  sponsorId: number,
  data: Partial<SponsorFormData>,
): Promise<Sponsor | null> {
  saving.value = true
  errors.value = {}
  generalError.value = null
  try {
    const updated = await apiUpdateSponsor(eventSlug, editionSlug, sponsorId, data)
    sponsors.value = sponsors.value.map((s) => (s.id === sponsorId ? updated : s))
    return updated
  } catch (e: unknown) {
    if (isValidationError(e)) {
      errors.value = e.response.data.errors
    } else {
      generalError.value = 'editions.sponsors.errors.save_failed'
    }
    return null
  } finally {
    saving.value = false
  }
}

async function deleteSponsor(
  eventSlug: string,
  editionSlug: string,
  sponsorId: number,
): Promise<boolean> {
  saving.value = true
  generalError.value = null
  try {
    await apiDeleteSponsor(eventSlug, editionSlug, sponsorId)
    sponsors.value = sponsors.value.filter((s) => s.id !== sponsorId)
    return true
  } catch {
    generalError.value = 'editions.sponsors.errors.delete_failed'
    return false
  } finally {
    saving.value = false
  }
}

async function uploadSponsorLogo(
  eventSlug: string,
  editionSlug: string,
  sponsorId: number,
  file: File,
): Promise<Sponsor | null> {
  logoUploading.value = true
  errors.value = {}
  generalError.value = null
  try {
    const updated = await apiUploadSponsorLogo(eventSlug, editionSlug, sponsorId, file)
    sponsors.value = sponsors.value.map((s) => (s.id === sponsorId ? updated : s))
    return updated
  } catch (e: unknown) {
    if (isValidationError(e)) {
      errors.value = e.response.data.errors
    } else {
      generalError.value = 'editions.sponsors.errors.logo_upload_failed'
    }
    return null
  } finally {
    logoUploading.value = false
  }
}

async function deleteSponsorLogo(
  eventSlug: string,
  editionSlug: string,
  sponsorId: number,
): Promise<boolean> {
  logoUploading.value = true
  generalError.value = null
  try {
    await apiDeleteSponsorLogo(eventSlug, editionSlug, sponsorId)
    sponsors.value = sponsors.value.map((s) =>
      s.id === sponsorId ? { ...s, logo_url: null } : s,
    )
    return true
  } catch {
    generalError.value = 'editions.sponsors.errors.logo_delete_failed'
    return false
  } finally {
    logoUploading.value = false
  }
}

export function useSponsors(): {
  sponsors: Ref<Sponsor[]>
  loading: Ref<boolean>
  saving: Ref<boolean>
  logoUploading: Ref<boolean>
  errors: Ref<Record<string, string[]>>
  generalError: Ref<string | null>
  fetchSponsors: (eventSlug: string, editionSlug: string) => Promise<void>
  createSponsor: (
    eventSlug: string,
    editionSlug: string,
    data: SponsorFormData,
  ) => Promise<Sponsor | null>
  updateSponsor: (
    eventSlug: string,
    editionSlug: string,
    sponsorId: number,
    data: Partial<SponsorFormData>,
  ) => Promise<Sponsor | null>
  deleteSponsor: (eventSlug: string, editionSlug: string, sponsorId: number) => Promise<boolean>
  uploadSponsorLogo: (
    eventSlug: string,
    editionSlug: string,
    sponsorId: number,
    file: File,
  ) => Promise<Sponsor | null>
  deleteSponsorLogo: (
    eventSlug: string,
    editionSlug: string,
    sponsorId: number,
  ) => Promise<boolean>
} {
  return {
    sponsors,
    loading,
    saving,
    logoUploading,
    errors,
    generalError,
    fetchSponsors,
    createSponsor,
    updateSponsor,
    deleteSponsor,
    uploadSponsorLogo,
    deleteSponsorLogo,
  }
}
